import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Users, Search, X, Loader2, Check, AlertCircle, Settings } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const colors = {
  blue: '#2E5E99',
  dark: '#0D2440',
  gray: '#5A7A9B',
  border: '#C8D8E8'
};

const HelperTeamsConfigDialog = ({ open, onOpenChange, onSaved }) => {
  const { token } = useAuth();
  const { notifyHelperTeamsChanged } = useData();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState(new Set());
  const [suggestedTeams, setSuggestedTeams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSelection, setOriginalSelection] = useState(new Set());

  useEffect(() => {
    if (open && token) {
      loadTeams();
    }
  }, [open, token]);

  const loadTeams = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/data/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { teams: allTeams, suggested_helper_teams, saved_helper_teams } = response.data;
      
      setTeams(allTeams || []);
      setSuggestedTeams(suggested_helper_teams || []);
      
      // Use saved selection if available, otherwise use suggested
      const initialSelection = new Set(
        saved_helper_teams !== null ? saved_helper_teams : suggested_helper_teams
      );
      setSelectedTeams(initialSelection);
      setOriginalSelection(new Set(initialSelection));
      setHasChanges(false);
    } catch (error) {
      console.error('Error loading teams:', error);
      toast.error('Fehler beim Laden der Teams');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTeam = (team) => {
    const newSelection = new Set(selectedTeams);
    if (newSelection.has(team)) {
      newSelection.delete(team);
    } else {
      newSelection.add(team);
    }
    setSelectedTeams(newSelection);
    
    // Check if there are changes
    const hasChange = newSelection.size !== originalSelection.size || 
      [...newSelection].some(t => !originalSelection.has(t)) ||
      [...originalSelection].some(t => !newSelection.has(t));
    setHasChanges(hasChange);
  };

  const handleSelectAll = () => {
    const filtered = filteredTeams;
    const newSelection = new Set(selectedTeams);
    filtered.forEach(t => newSelection.add(t));
    setSelectedTeams(newSelection);
    setHasChanges(true);
  };

  const handleDeselectAll = () => {
    const filtered = filteredTeams;
    const newSelection = new Set(selectedTeams);
    filtered.forEach(t => newSelection.delete(t));
    setSelectedTeams(newSelection);
    setHasChanges(true);
  };

  const handleUseSuggested = () => {
    setSelectedTeams(new Set(suggestedTeams));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/data/helper-teams`, 
        [...selectedTeams],
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setOriginalSelection(new Set(selectedTeams));
      setHasChanges(false);
      toast.success(`${selectedTeams.size} Hilfsteams gespeichert`);
      
      // Notify DataContext that helper teams changed - Reports page will reload data
      if (notifyHelperTeamsChanged) {
        notifyHelperTeamsChanged();
      }
      
      if (onSaved) {
        onSaved([...selectedTeams]);
      }
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving helper teams:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('Sie haben ungespeicherte Änderungen. Trotzdem schließen?')) {
        setSelectedTeams(originalSelection);
        setHasChanges(false);
        onOpenChange(false);
      }
    } else {
      onOpenChange(false);
    }
  };

  const filteredTeams = teams.filter(team => 
    team.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const regularTeams = filteredTeams.filter(t => !selectedTeams.has(t));
  const helperTeams = filteredTeams.filter(t => selectedTeams.has(t));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh]" data-testid="helper-teams-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" style={{ color: colors.blue }} />
            Hilfsteams konfigurieren
          </DialogTitle>
          <DialogDescription>
            Wählen Sie aus, welche Teams als "Hilfsteams" behandelt werden sollen. 
            Diese werden in Reports optional ausgeblendet.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.blue }} />
            <span className="ml-3" style={{ color: colors.gray }}>Lade Teams...</span>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Search and Actions */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: colors.gray }} />
                <input
                  type="text"
                  placeholder="Team suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border text-sm"
                  style={{ borderColor: colors.border, color: colors.dark }}
                  data-testid="helper-teams-search"
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <X className="h-4 w-4" style={{ color: colors.gray }} />
                  </button>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleUseSuggested}
                data-testid="use-suggested-btn"
              >
                Vorschläge verwenden ({suggestedTeams.length})
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleSelectAll}
              >
                Alle sichtbaren auswählen
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleDeselectAll}
                className="text-red-500"
              >
                Alle sichtbaren abwählen
              </Button>
              <div className="flex-1 text-right">
                <Badge variant="secondary" className="text-xs">
                  {selectedTeams.size} Hilfsteams / {teams.length} Teams
                </Badge>
              </div>
            </div>

            {/* Info Box */}
            <div className="flex items-start gap-2 p-3 rounded-lg text-sm" style={{ background: '#f0f7ff', border: '1px solid #2E5E99' }}>
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: colors.blue }} />
              <div style={{ color: colors.dark }}>
                <strong>Automatische Erkennung:</strong> Teams mit Präfixen wie <code>xxx_</code>, <code>z_</code>, <code>zzz_</code> 
                oder <code>alle</code> im Namen werden automatisch vorgeschlagen.
              </div>
            </div>

            {/* Teams Lists */}
            <div className="grid grid-cols-2 gap-4">
              {/* Selected Helper Teams */}
              <div>
                <p className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: colors.dark }}>
                  <span className="w-3 h-3 rounded bg-amber-500"></span>
                  Hilfsteams ({helperTeams.length})
                </p>
                <ScrollArea className="h-[280px] border rounded-lg p-2" style={{ borderColor: '#f59e0b', background: '#fffbeb' }}>
                  {helperTeams.length > 0 ? (
                    <div className="space-y-1">
                      {helperTeams.map(team => (
                        <label 
                          key={team}
                          className="flex items-center gap-2 p-2 rounded hover:bg-amber-100 cursor-pointer"
                        >
                          <Checkbox
                            checked={true}
                            onCheckedChange={() => handleToggleTeam(team)}
                            data-testid={`team-checkbox-${team}`}
                          />
                          <span className="text-sm truncate" style={{ color: colors.dark }} title={team}>
                            {team}
                          </span>
                          {suggestedTeams.includes(team) && (
                            <Badge variant="outline" className="text-xs ml-auto">Auto</Badge>
                          )}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-sm" style={{ color: colors.gray }}>
                      {searchTerm ? 'Keine Treffer' : 'Keine Hilfsteams ausgewählt'}
                    </p>
                  )}
                </ScrollArea>
              </div>

              {/* Regular Teams */}
              <div>
                <p className="text-sm font-medium mb-2 flex items-center gap-2" style={{ color: colors.dark }}>
                  <span className="w-3 h-3 rounded bg-green-500"></span>
                  Reguläre Teams ({regularTeams.length})
                </p>
                <ScrollArea className="h-[280px] border rounded-lg p-2" style={{ borderColor: '#22c55e', background: '#f0fdf4' }}>
                  {regularTeams.length > 0 ? (
                    <div className="space-y-1">
                      {regularTeams.map(team => (
                        <label 
                          key={team}
                          className="flex items-center gap-2 p-2 rounded hover:bg-green-100 cursor-pointer"
                        >
                          <Checkbox
                            checked={false}
                            onCheckedChange={() => handleToggleTeam(team)}
                            data-testid={`team-checkbox-${team}`}
                          />
                          <span className="text-sm truncate" style={{ color: colors.dark }} title={team}>
                            {team}
                          </span>
                          {suggestedTeams.includes(team) && (
                            <Badge variant="outline" className="text-xs ml-auto">Auto</Badge>
                          )}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-sm" style={{ color: colors.gray }}>
                      {searchTerm ? 'Keine Treffer' : 'Alle Teams sind als Hilfsteams markiert'}
                    </p>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between">
          <div>
            {hasChanges && (
              <span className="text-sm text-amber-600">Ungespeicherte Änderungen</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !hasChanges}
              style={{ background: colors.blue }}
              data-testid="save-helper-teams-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Speichern ({selectedTeams.size})
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HelperTeamsConfigDialog;
